import { useEffect, useRef, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Crown, Loader2, Download, ShieldCheck } from "lucide-react";

const SOURCES = [
  { id: "cortex", label: "CORTEX memories" },
  { id: "chats", label: "KIT chats + approved Review items" },
  { id: "audit", label: "KIT audit log" },
  { id: "board", label: "Board issues" },
  { id: "docs", label: "Docs / specs (not available yet)", disabled: true },
];

const Row = ({ k, v }) => (
  <div className="flex justify-between font-mono text-[11px]">
    <span className="text-slate-400 truncate">{k}</span>
    <span className="text-slate-200 shrink-0 ml-2">{v}</span>
  </div>
);

// Empire source: reads our own systems (MrHelm) through read-scoped export
// endpoints. The device token is only held in this form and in the running
// ingest task; it is cleared as soon as the job starts and never stored.
export default function EmpireSource({ onStarted }) {
  const [url, setUrl] = useState("https://mrhelm.com");
  const [token, setToken] = useState("");
  const [sources, setSources] = useState(["cortex", "chats", "audit", "board"]);
  const [evalPct, setEvalPct] = useState(10);
  const [genQa, setGenQa] = useState(true);
  const [qaMax, setQaMax] = useState(150);
  const [teacher, setTeacher] = useState("qwen3:8b");
  const [ollama, setOllama] = useState("http://localhost:11434");
  const [busy, setBusy] = useState(false);
  const [crawlId, setCrawlId] = useState(null);
  const [report, setReport] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const toggle = (id) =>
    setSources((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const poll = (id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.empireReport(id);
        setReport(r);
        if (r.status !== "running") {
          clearInterval(timer.current);
          setBusy(false);
          onStarted?.();
          if (r.status === "completed") toast.success("Empire ingest finished");
          else toast.error(r.error || "Empire ingest failed");
        }
      } catch {
        clearInterval(timer.current);
        setBusy(false);
      }
    }, 3000);
  };

  const start = async () => {
    setBusy(true);
    setReport(null);
    try {
      const r = await api.startEmpire({
        mrhelm_url: url.trim(), token: token.trim(), sources,
        eval_pct: Number(evalPct), generate_qa: genQa, qa_max_passages: Number(qaMax),
        teacher_model: teacher.trim(), ollama_url: ollama.trim(),
      });
      setToken("");
      setCrawlId(r.crawl_id);
      onStarted?.();
      poll(r.crawl_id);
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start the Empire ingest");
    }
  };

  const download = async (opts, name) => {
    try {
      await downloadFile(api.exportUrl(crawlId, { fmt: "jsonl", dedupe: false, ...opts }), name);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const rep = report?.empire_report;
  const done = report?.status === "completed" && rep;

  return (
    <div data-testid="empire-source-panel" className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3.5 space-y-3">
      <div className="flex items-center gap-2">
        <Crown className="h-3.5 w-3.5 text-amber-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Empire Source</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600">read-only, read-scoped token</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">MrHelm URL</Label>
          <Input data-testid="empire-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Device token (mhd_..., read scope)</Label>
          <Input data-testid="empire-token-input" type="password" autoComplete="off" value={token}
            onChange={(e) => setToken(e.target.value)} placeholder="mhd_..."
            className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
        </div>
      </div>

      <div className="space-y-1">
        {SOURCES.map((s) => (
          <label key={s.id} className={cn("flex items-center gap-2 text-xs", s.disabled ? "text-slate-600" : "text-slate-300")}>
            <input type="checkbox" disabled={s.disabled || busy} checked={sources.includes(s.id)}
              onChange={() => toggle(s.id)} data-testid={`empire-source-${s.id}`} />
            {s.label}
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Eval split % (real examples only)</Label>
          <Input data-testid="empire-eval-input" type="number" min={0} max={50} value={evalPct}
            onChange={(e) => setEvalPct(e.target.value)} className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
        </div>
        <div className="flex items-end justify-between rounded-md border border-border bg-[#0B0F18] px-3 py-2">
          <Label className="text-xs text-slate-400">Generate Q&amp;A (Ollama teacher)</Label>
          <Switch data-testid="empire-qa-switch" checked={genQa} onCheckedChange={setGenQa} />
        </div>
      </div>

      {genQa && (
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Teacher model</Label>
            <Input value={teacher} onChange={(e) => setTeacher(e.target.value)}
              className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Ollama URL</Label>
            <Input value={ollama} onChange={(e) => setOllama(e.target.value)}
              className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Max passages</Label>
            <Input type="number" min={0} max={2000} value={qaMax} onChange={(e) => setQaMax(e.target.value)}
              className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
          </div>
        </div>
      )}

      <Button data-testid="empire-start-button" onClick={start}
        disabled={busy || !token.trim().startsWith("mhd_") || sources.length === 0}
        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Crown className="h-4 w-4 mr-1.5" />}
        {busy ? "Ingesting..." : "Ingest Empire data"}
      </Button>

      {report && (
        <div data-testid="empire-report" className="rounded border border-border bg-[#04060A] p-2.5 space-y-2">
          <div className="flex items-center gap-1.5 font-mono text-[11px] text-slate-300">
            <ShieldCheck className="h-3 w-3 text-emerald-400" />
            {report.name} — {report.status}
          </div>
          {report.error && <p className="font-mono text-[11px] text-rose-300">{report.error}</p>}
          {rep && (
            <>
              <div className="space-y-0.5">
                {Object.entries(rep.per_source).map(([k, v]) => <Row key={k} k={`source: ${k}`} v={v} />)}
                {Object.entries(rep.training_records).map(([k, v]) => <Row key={k} k={`training ${k}`} v={v} />)}
                <Row k={`eval set (${rep.eval_pct}%, real only)`} v={rep.eval_size} />
                <Row k="Q&A generated / not-in-docs" v={`${rep.qa_generated} / ${rep.qa_not_in_documents}`} />
                <Row k="secrets redacted" v={Object.keys(rep.secrets_redacted).length
                  ? Object.entries(rep.secrets_redacted).map(([k, v]) => `${k}:${v}`).join(" ") : "none"} />
              </div>
              <details className="font-mono text-[11px] text-slate-400">
                <summary className="cursor-pointer">tags ({Object.keys(rep.per_tag).length})</summary>
                <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                  {Object.entries(rep.per_tag).map(([k, v]) => <Row key={k} k={k} v={v} />)}
                </div>
              </details>
            </>
          )}
          {done && (
            <div className="grid grid-cols-3 gap-2">
              <Button size="sm" variant="outline" data-testid="empire-dl-knowledge"
                onClick={() => download({ shape: "knowledge" }, "empire-knowledge.jsonl")}>
                <Download className="h-3 w-3 mr-1" />Knowledge
              </Button>
              <Button size="sm" variant="outline" data-testid="empire-dl-train"
                onClick={() => download({ shape: "training", split: "train" }, "empire-train.jsonl")}>
                <Download className="h-3 w-3 mr-1" />Train
              </Button>
              <Button size="sm" variant="outline" data-testid="empire-dl-eval"
                onClick={() => download({ shape: "training", split: "eval" }, "empire-eval.jsonl")}>
                <Download className="h-3 w-3 mr-1" />Eval
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
